import { ExportJobStatus } from "@/shared/domain/exportJobs/ExportJobRecord";
import { FileService } from "@/shared/domain/files/FileService";

import { UIExportJob } from "./types";

const TERMINAL = new Set<ExportJobStatus>(["completed", "failed", "expired"]);
const POLL_INTERVAL_MS = 2000;

type Listener = () => void;

export interface ExportSnapshot {
  jobs: UIExportJob[];
  isToastVisible: boolean;
}

/**
 * Manages async ZIP export jobs: polling, state transitions, and download
 * triggering. Plain TypeScript class — no React dependency — so it can be
 * tested without a component tree.
 *
 * Implements the useSyncExternalStore contract via `subscribe` / `getSnapshot`.
 */
export class FileExportService {
  #getFileService: () => FileService;
  #onDownload: (url: string) => void;
  #jobs: Map<string, UIExportJob> = new Map();
  #intervals: Map<string, ReturnType<typeof setInterval>> = new Map();
  #downloadTriggered: Set<string> = new Set();
  #listeners: Set<Listener> = new Set();
  #isToastVisible = false;
  #snapshot: ExportSnapshot = { jobs: [], isToastVisible: false };

  constructor(
    getFileService: () => FileService,
    onDownload: (url: string) => void,
  ) {
    this.#getFileService = getFileService;
    this.#onDownload = onDownload;
  }

  /**
   * Register a new export job, prune any previously completed/failed jobs,
   * open the toast, and begin polling immediately.
   */
  startJob(jobId: string, label: string): void {
    // Remove terminal jobs from a previous export session so the toast reopens
    // showing only the new job (and any still-active ones).
    for (const [id, job] of this.#jobs) {
      if (TERMINAL.has(job.status as ExportJobStatus)) {
        clearInterval(this.#intervals.get(id));
        this.#intervals.delete(id);
        this.#jobs.delete(id);
      }
    }

    this.#jobs.set(jobId, {
      id: jobId,
      label,
      status: "pending",
      bytesUploaded: 0,
      totalBytes: 0,
      created: Date.now(),
    });
    this.#isToastVisible = true;

    // Fire an immediate first poll, then keep going at the regular interval.
    void this.#poll(jobId);
    const interval = setInterval(
      () => void this.#poll(jobId),
      POLL_INTERVAL_MS,
    );
    this.#intervals.set(jobId, interval);

    this.#commit();
  }

  /** Hide the toast without affecting job state or polling. */
  closeToast(): void {
    this.#isToastVisible = false;
    this.#commit();
  }

  /** True if any job has not yet reached a terminal status. */
  hasActiveJobs(): boolean {
    for (const job of this.#jobs.values()) {
      if (!TERMINAL.has(job.status as ExportJobStatus)) {
        return true;
      }
    }
    return false;
  }

  /** useSyncExternalStore — subscribe */
  subscribe = (listener: Listener): (() => void) => {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  };

  /** useSyncExternalStore — getSnapshot */
  getSnapshot = (): ExportSnapshot => this.#snapshot;

  /** Clear all intervals. Call on provider unmount or logout. */
  destroy(): void {
    for (const interval of this.#intervals.values()) {
      clearInterval(interval);
    }
    this.#intervals.clear();
  }

  async #poll(jobId: string): Promise<void> {
    const existing = this.#jobs.get(jobId);
    if (!existing) {
      return;
    }

    try {
      const data = await this.#getFileService().getExportJob(jobId);
      const updated: UIExportJob = {
        ...existing,
        status: data.status,
        bytesUploaded: data.bytes_uploaded ?? existing.bytesUploaded,
        totalBytes: data.total_bytes ?? existing.totalBytes,
        downloadUrl: data.download_url,
        error: data.error,
      };
      this.#jobs.set(jobId, updated);

      if (TERMINAL.has(data.status)) {
        clearInterval(this.#intervals.get(jobId));
        this.#intervals.delete(jobId);
        if (
          data.status === "completed" &&
          data.download_url &&
          !this.#downloadTriggered.has(jobId)
        ) {
          this.#downloadTriggered.add(jobId);
          this.#onDownload(data.download_url);
        }
      }
    } catch (err) {
      // HttpClient already retries transiently; a thrown error means retries
      // are exhausted — surface as a failed job and stop polling.
      clearInterval(this.#intervals.get(jobId));
      this.#intervals.delete(jobId);
      this.#jobs.set(jobId, {
        ...existing,
        status: "failed",
        error: err instanceof Error ? err.message : "Unknown error",
      });
    }

    this.#commit();
  }

  /** Recompute the snapshot and notify listeners only if something changed. */
  #commit(): void {
    const nextJobs = Array.from(this.#jobs.values()).sort(
      (a, b) => a.created - b.created,
    );
    const nextVisible = this.#isToastVisible;
    const prev = this.#snapshot;

    const unchanged =
      prev.isToastVisible === nextVisible &&
      prev.jobs.length === nextJobs.length &&
      prev.jobs.every(
        (j, i) =>
          j.status === nextJobs[i].status &&
          j.bytesUploaded === nextJobs[i].bytesUploaded &&
          j.totalBytes === nextJobs[i].totalBytes,
      );

    if (unchanged) {
      return;
    }

    this.#snapshot = { jobs: nextJobs, isToastVisible: nextVisible };
    this.#listeners.forEach((l) => l());
  }
}
